<script>
	// 단일 시리즈 미니 차트 (line=면적+라인 / bar=막대). shadcn(--sb-*) 토큰.
	// 컨테이너 폭 실측(bind:clientWidth)으로 왜곡 없는 반응형 + 쉬운 히트테스트.
	// dataviz: 단일 시리즈 = 단일 색(primary), 범례 없음(제목이 이름), 값은 툴팁/축라벨로.
	export let title = "";
	export let subtitle = "";
	export let kind = "line"; // line | bar
	export let series = []; // [{label, value}]

	const H = 168;
	const PAD = { l: 30, r: 10, t: 12, b: 22 };

	let w = 0;
	let hover = null; // {x, y, label, value}

	const niceMax = (v) => {
		if (v <= 5) return 5;
		const p = Math.pow(10, String(Math.round(v)).length - 1);
		return Math.ceil(v / p) * p;
	};

	$: n = series.length;
	$: plotW = Math.max(0, w - PAD.l - PAD.r);
	$: plotH = H - PAD.t - PAD.b;
	$: max = niceMax(Math.max(1, ...series.map((d) => d.value)));
	$: stepX = n > 1 ? plotW / (n - 1) : 0;
	$: slot = n > 0 ? plotW / n : 0;
	$: bw = slot * 0.58;
	$: pts = series.map((d, i) => {
		const cx = PAD.l + (n > 1 ? i * stepX : plotW / 2);
		const cy = PAD.t + plotH * (1 - d.value / max);
		return { ...d, i, cx, cy, barX: PAD.l + slot * i + (slot - bw) / 2, barTop: cy, barH: PAD.t + plotH - cy };
	});
	$: grid = [0, 0.5, 1].map((f) => ({ y: PAD.t + plotH * f, v: Math.round(max * (1 - f)) }));
	$: labelEvery = n <= 8 ? 1 : n <= 16 ? 2 : Math.ceil(n / 8);

	const linePath = (p) => p.map((q, i) => `${i ? "L" : "M"}${q.cx},${q.cy}`).join(" ");
	const areaPath = () => {
		if (!pts.length) return "";
		const base = PAD.t + plotH;
		return `M${pts[0].cx},${base} ${pts.map((q) => `L${q.cx},${q.cy}`).join(" ")} L${pts[pts.length - 1].cx},${base} Z`;
	};

	const onMove = (e) => {
		if (!pts.length) return;
		const rect = e.currentTarget.getBoundingClientRect();
		const mx = e.clientX - rect.left;
		let i = Math.round((mx - PAD.l) / (plotW / Math.max(n - 1, 1)));
		i = Math.min(pts.length - 1, Math.max(0, i));
		const p = pts[i];
		hover = { i, x: kind === "bar" ? p.barX + bw / 2 : p.cx, y: p.barTop, label: p.label, value: p.value };
	};
	const fmt = (v) => (v || 0).toLocaleString();
</script>

<div class="card">
	<div class="card-h">
		<span class="card-t">{title}</span>
		{#if subtitle}<span class="card-s">{subtitle}</span>{/if}
	</div>
	<div class="plot" bind:clientWidth={w} on:mousemove={onMove} on:mouseleave={() => (hover = null)} role="img" aria-label={`${title} 차트`}>
		<svg width="100%" height={H} viewBox={`0 0 ${w || 1} ${H}`} preserveAspectRatio="none">
			{#each grid as gl}
				<line x1={PAD.l} y1={gl.y} x2={(w || 1) - PAD.r} y2={gl.y} class="grid" />
				<text x={PAD.l - 6} y={gl.y + 3} class="ytick" text-anchor="end">{gl.v}</text>
			{/each}

			{#if kind === "line"}
				<path d={areaPath()} class="area" />
				<path d={linePath(pts)} class="line" />
				{#each pts as p}
					<circle cx={p.cx} cy={p.cy} r={hover && hover.i === p.i ? 4.5 : 0} class="dot" />
				{/each}
			{:else}
				{#each pts as p}
					<rect x={p.barX} y={p.barTop} width={bw} height={Math.max(0, p.barH)} rx="3" class="bar" class:hot={hover && hover.i === p.i} />
				{/each}
			{/if}

			{#each pts as p}
				{#if p.i % labelEvery === 0}
					<text x={kind === "bar" ? p.barX + bw / 2 : p.cx} y={H - 6} class="xtick" text-anchor="middle">{p.label}</text>
				{/if}
			{/each}
		</svg>

		{#if hover}
			<div class="tip" style="left:{hover.x}px; top:{hover.y}px">
				<div class="tip-v">{fmt(hover.value)}</div>
				<div class="tip-l">{hover.label}</div>
			</div>
		{/if}
	</div>
</div>

<style>
	.card {
		border: 1px solid hsl(var(--sb-border));
		border-radius: 8px;
		padding: 12px;
		background: hsl(var(--sb-card));
	}
	.card-h {
		display: flex;
		align-items: baseline;
		gap: 8px;
		margin-bottom: 6px;
	}
	.card-t {
		font-size: 13px;
		font-weight: 600;
		color: hsl(var(--sb-foreground));
	}
	.card-s {
		font-size: 11px;
		color: hsl(var(--sb-muted-foreground));
	}
	.plot {
		position: relative;
		width: 100%;
	}
	.grid {
		stroke: hsl(var(--sb-border));
		stroke-width: 1;
		opacity: 0.6;
	}
	.ytick,
	.xtick {
		fill: hsl(var(--sb-muted-foreground));
		font-size: 9px;
		font-variant-numeric: tabular-nums;
	}
	.area {
		fill: hsl(var(--sb-primary) / 0.12);
		stroke: none;
	}
	.line {
		fill: none;
		stroke: hsl(var(--sb-primary));
		stroke-width: 2;
		stroke-linejoin: round;
		stroke-linecap: round;
	}
	.dot {
		fill: hsl(var(--sb-primary));
		stroke: hsl(var(--sb-card));
		stroke-width: 2;
	}
	.bar {
		fill: hsl(var(--sb-primary) / 0.85);
	}
	.bar.hot {
		fill: hsl(var(--sb-primary));
	}
	.tip {
		position: absolute;
		transform: translate(-50%, -130%);
		pointer-events: none;
		background: hsl(var(--sb-foreground));
		color: hsl(var(--sb-background));
		padding: 3px 8px;
		border-radius: 6px;
		font-size: 11px;
		white-space: nowrap;
		box-shadow: 0 4px 12px rgba(16, 24, 40, 0.18);
		z-index: 2;
	}
	.tip-v {
		font-weight: 700;
		font-variant-numeric: tabular-nums;
		line-height: 1.2;
	}
	.tip-l {
		opacity: 0.7;
		font-size: 10px;
	}
</style>
